import { makeObservable, observable } from 'mobx';

import type {
  ICollectionStore,
  IListOptions,
  IListParams,
  ListResponse,
  TConstraints,
} from '@feathr/rachis';
import { isWretchError } from '@feathr/rachis';
import { Collection, Model, wretch } from '@feathr/rachis';

import type { IPredicate, IPredicateGroup } from '../segments';
import type { IBreadcrumb } from './breadcrumbs.attributes';
import type { FlavorKeys } from './breadcrumbs.types';

/** Suggestions for predicates are determined by the actual flavors
 * seen by the account.
 */
export interface IBreadcrumbSuggestion extends Record<string, unknown> {
  occurrence: number;
  value: string;
}

/**
 * Aggregate donation statistics returned by `GET /donations/stats`.
 * Computed live from breadcrumbs (donation flavor), so reflects the current
 * state of donations rather than the lazy `stats_cache` on each donation form.
 */
export interface IDonationStats extends Record<string, unknown> {
  /** Sum of all matching donation amounts in account currency. */
  total_donation_volume: number;
  /** Sum of growth credits earned across matching donations. */
  total_growth_credits: number;
}

/** Breadcrumbs are queried via ElasticSearch on the backend.
 * The interface includes predicates and lookback parameters for complex filtering.
 */
export interface IBreadcrumbListParams extends IListParams<IBreadcrumb> {
  predicates: Array<IPredicate | IPredicateGroup>;
  mode: 'match_all' | 'match_any';
  lookback_mode: 'absolute' | 'unbounded' | 'relative' | 'before';
  lookback_value: string | number;
  exclude: ['breadcrumbs'];
}

export class Breadcrumb extends Model<IBreadcrumb> {
  public readonly className = 'Breadcrumb';

  public get constraints(): TConstraints<IBreadcrumb> {
    return {};
  }

  /** Handles creating pseudo-flavors for UI management */
  public get flavor(): FlavorKeys {
    const flvr = this.get('flvr');
    if (flvr?.startsWith('form')) {
      const type = this.get('form_type');
      if (type === 'donation') {
        return `donation_${flvr}` as FlavorKeys;
      }
    }
    return flvr;
  }
}

export class Breadcrumbs extends Collection<Breadcrumb> {
  @observable public stream: Breadcrumb[] = [];

  constructor(initialModels: Array<Partial<IBreadcrumb>>, store: ICollectionStore = {}) {
    super(initialModels, store);

    makeObservable(this);
  }

  public getClassName(): string {
    return 'breadcrumbs';
  }

  public getModel(attributes: Partial<IBreadcrumb>): Breadcrumb {
    return new Breadcrumb(attributes);
  }

  /**
   * @param listId Use the useId() hook to generate a unique listID.
   */
  public override list(
    params: Partial<IBreadcrumbListParams>,
    options: Partial<IListOptions> = {},
    listId?: string,
  ): ListResponse<Breadcrumb> {
    return super.list(params, options, listId);
  }

  public async getSuggestions(path: string, query: string): Promise<IBreadcrumbSuggestion[]> {
    const response = await wretch<IBreadcrumbSuggestion[]>(
      this.url('suggestions', [path, encodeURIComponent(query)]),
      {
        method: 'GET',
        headers: this.getHeaders(),
      },
    );

    if (isWretchError(response)) {
      throw response.error;
    }
    return response.data;
  }

  /**
   * Fetch live donation totals from the backend's `/donations/stats` endpoint.
   * Aggregates donation breadcrumbs across the current account.
   */
  public async getDonationStats(): Promise<IDonationStats> {
    const response = await wretch<IDonationStats>(`${this.getHostname()}donations/stats`, {
      method: 'GET',
      headers: this.getHeaders(),
    });

    if (isWretchError(response)) {
      throw response.error;
    }

    return response.data;
  }

  public override url(): string;

  /**
   * @param value (required) person id
   */
  public override url(variant: 'person' | 'person.flavors', value: string): string;

  /**
   * @param value (required) [ path, query ]
   */
  public override url(variant: 'suggestions', value: [string, string]): string;

  public override url(
    variant?: 'person' | 'person.flavors' | 'suggestions',
    value?: string | string[],
  ): string {
    if (
      variant &&
      ['person', 'person.flavors', 'suggestions'].includes(variant) &&
      (Array.isArray(value) ? !value.length : !value)
    ) {
      throw new Error(`Value is required when variant is "${variant}".`);
    }

    switch (variant) {
      case 'person':
        return `${this.getHostname()}persons/${value}/breadcrumbs/`;

      case 'person.flavors':
        return `${this.getHostname()}persons/${value}/breadcrumbs/flvrs`;

      case 'suggestions':
        if (!Array.isArray(value)) {
          throw new Error(`Value should be an array when variant is "${variant}".`);
        }
        return `${this.getHostname()}${value[0]}/?q=${encodeURIComponent(value[1])}`;

      default:
        return `${this.getHostname()}breadcrumbs/page/`;
    }
  }
}
